import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Default-Abschlusssatz (konsistent Sie-Form) - identisch zu backend DEFAULT_CLOSING_NOTE
export const DEFAULT_CLOSING_NOTE =
  "Vielen Dank für Ihr Vertrauen — bei Rückfragen stehe ich Ihnen jederzeit gerne zur Verfügung.";

/**
 * Erstellt eine PDF-Rechnung (für klein + voll gleiches Layout).
 */
export function generateInvoicePdf({ invoice, settings }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const SLATE = [30, 41, 59];
  const SLATE_LIGHT = [100, 116, 139];
  const BORDER = [203, 213, 225];

  const s = settings || {};
  const taxOrVat = s.ust_id || s.tax_id || s.vat_id || "";

  // -------- Firmen-Kopf (oben rechts) --------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...SLATE);
  doc.text(s.company_name || "", pageW - 14, 20, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE_LIGHT);
  let y = 25;
  if (s.street) { doc.text(s.street, pageW - 14, y, { align: "right" }); y += 4; }
  if (s.postal_code || s.city) {
    doc.text(`${s.postal_code || ""} ${s.city || ""}`.trim(), pageW - 14, y, { align: "right" });
    y += 4;
  }
  if (s.phone) { doc.text(`Tel: ${s.phone}`, pageW - 14, y, { align: "right" }); y += 4; }
  if (s.email) { doc.text(s.email, pageW - 14, y, { align: "right" }); y += 4; }

  // -------- Kundenadresse (oben links) --------
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE_LIGHT);
  const senderLine = [s.company_name, s.street, `${s.postal_code || ""} ${s.city || ""}`.trim()].filter(Boolean).join(" · ");
  if (senderLine) doc.text(senderLine, 14, 40);

  doc.setDrawColor(...SLATE_LIGHT);
  doc.line(14, 41, 100, 41);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...SLATE);
  let cy = 50;
  if (invoice.customer_name && invoice.customer_name.trim()) {
    doc.text(invoice.customer_name, 14, cy); cy += 5;
    if (invoice.customer_street) { doc.text(invoice.customer_street, 14, cy); cy += 5; }
    if (invoice.customer_postal_code || invoice.customer_city) {
      doc.text(`${invoice.customer_postal_code || ""} ${invoice.customer_city || ""}`.trim(), 14, cy);
      cy += 5;
    }
  }

  // -------- Titel + Rechnungsnummer + Datum --------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...SLATE);
  const titleY = Math.max(cy + 10, 85);
  doc.text("Rechnung", 14, titleY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE);
  let infoY = titleY + 7;
  doc.text(`Rechnungsnummer: ${invoice.invoice_number}`, 14, infoY);
  doc.text(`Datum: ${new Date(invoice.date).toLocaleDateString("de-DE")}`, pageW - 14, infoY, { align: "right" });

  // Bei Rechnungen > 250 € zusätzlich Steuernummer/USt-IdNr. prominent zeigen
  const totalBruttoPreview = Number(invoice.total_brutto) || 0;
  if (totalBruttoPreview > 250 && taxOrVat) {
    infoY += 5;
    doc.text(`Steuernummer / USt-IdNr.: ${taxOrVat}`, 14, infoY);
  }

  // -------- Positionen-Tabelle --------
  const tableStart = infoY + 10;

  const head = [["Pos.", "Artikel", "Menge", "Einzelpreis", "MwSt.", "Gesamt (brutto)"]];

  const body = (invoice.items || []).map((item, idx) => {
    const gesamt = item.quantity * item.price_brutto;
    return [
      (idx + 1).toString(),
      item.article,
      item.quantity.toString(),
      fmtEur(item.price_brutto),
      `${item.ust_percent || 0} %`,
      fmtEur(gesamt),
    ];
  });

  autoTable(doc, {
    startY: tableStart,
    head,
    body,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 3, textColor: SLATE, lineColor: BORDER },
    headStyles: { fillColor: SLATE, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 30, halign: "right" },
      4: { cellWidth: 18, halign: "center" },
      5: { cellWidth: 32, halign: "right" },
    },
  });

  let afterTable = doc.lastAutoTable.finalY + 10;

  // -------- MwSt-Berechnung (aus Brutto je Satz) --------
  const bruttoBySatz = {};
  (invoice.items || []).forEach(item => {
    const satz = item.ust_percent || 0;
    const brutto = item.quantity * item.price_brutto;
    bruttoBySatz[satz] = (bruttoBySatz[satz] || 0) + brutto;
  });

  const totalBrutto = Object.values(bruttoBySatz).reduce((a, b) => a + b, 0);
  let totalMwst = 0;
  const mwstDetails = []; // { satz, betrag }
  Object.keys(bruttoBySatz)
    .map(k => parseFloat(k))
    .sort((a, b) => a - b)
    .forEach(satz => {
      const brutto = bruttoBySatz[satz];
      if (satz > 0) {
        const mwst = brutto - (brutto / (1 + satz / 100));
        totalMwst += mwst;
        mwstDetails.push({ satz, betrag: mwst });
      }
    });
  const totalNetto = totalBrutto - totalMwst;

  // -------- Summen-Block (rechts) --------
  const sumX = pageW - 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE);

  // Netto Gesamtbetrag
  doc.text("Netto-Gesamtbetrag:", sumX - 45, afterTable, { align: "right" });
  doc.text(fmtEur(totalNetto), sumX, afterTable, { align: "right" });
  afterTable += 6;

  // MwSt-Zeilen (nur vorhandene Sätze)
  mwstDetails.forEach(m => {
    doc.text(`zzgl. ${m.satz} % MwSt.:`, sumX - 45, afterTable, { align: "right" });
    doc.text(fmtEur(m.betrag), sumX, afterTable, { align: "right" });
    afterTable += 6;
  });

  // Gesamtbetrag in Box
  afterTable += 2;
  doc.setDrawColor(...SLATE);
  doc.setLineWidth(0.5);
  doc.setFillColor(241, 245, 249);
  doc.rect(sumX - 70, afterTable, 70, 10, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Gesamtbetrag:", sumX - 65, afterTable + 7);
  doc.text(fmtEur(totalBrutto), sumX - 2, afterTable + 7, { align: "right" });
  afterTable += 18;

  // -------- Zahlungshinweis + Bankverbindung (nur bei aktiver Checkbox) --------
  if (invoice.include_bank_details && (s.bank_name || s.iban)) {
    afterTable += 3;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...SLATE);
    const dueTxt = invoice.due_date
      ? ` bis zum ${new Date(invoice.due_date).toLocaleDateString("de-DE")}`
      : "";
    doc.text(
      `Bitte überweisen Sie den Betrag${dueTxt} auf folgendes Konto:`,
      14, afterTable
    );
    afterTable += 6;
    doc.setFont("helvetica", "bold");
    doc.text("Bankverbindung:", 14, afterTable);
    afterTable += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    if (s.bank_name) { doc.text(`Bank: ${s.bank_name}`, 14, afterTable); afterTable += 4; }
    if (s.iban) { doc.text(`IBAN: ${s.iban}`, 14, afterTable); afterTable += 4; }
    if (s.bic) { doc.text(`BIC: ${s.bic}`, 14, afterTable); afterTable += 4; }
  }

  // -------- Abschlusssatz (immer, individuell oder Default) --------
  const closing = (invoice.closing_note || "").trim() || DEFAULT_CLOSING_NOTE;
  afterTable += 8;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE);
  // einfache Zeilenumbruch-Logik für längere Abschlusssätze
  const closingLines = doc.splitTextToSize(closing, pageW - 28);
  doc.text(closingLines, 14, afterTable);

  // -------- Footer --------
  const footY = pageH - 15;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.line(14, footY, pageW - 14, footY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE_LIGHT);
  const footerParts = [];
  if (s.company_name) footerParts.push(s.company_name);
  if (taxOrVat) footerParts.push(`St.-Nr. / USt-IdNr.: ${taxOrVat}`);
  const footerText = footerParts.join(" · ");
  if (footerText) doc.text(footerText, pageW / 2, footY + 5, { align: "center" });

  return doc;
}

export function downloadInvoicePdf({ invoice, settings }) {
  const doc = generateInvoicePdf({ invoice, settings });
  doc.save(`${invoice.invoice_number}.pdf`);
}

function fmtEur(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
